import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { AolSectionDataUpdateDtoFactory } from '../../factory/AolSectionDataUpdateDtoFactory';
import { AolSectionDataUpdateDto } from '../../types/Dto/AolSectionDataUpdateDto';
import { NoonReportSectionEnum, NoonReportTypeEnum } from '../../types/ViewModel/AolEnums';
import { AolDataItem } from '../../types/ViewModel/AolItem';
import { NoonReportSectionData } from '../../types/ViewModel/NoonReportSectionData';
import { getAllAolSections, getSectionsInColumn, isSectionVisible } from '../../utils/aolSectionHelper';
import { getDateHour } from 'odfjell-date-time-utils/dateTimeUtils';
import { DateTimeFormat, formatDateTime } from 'odfjell-date-time-utils/dateTimeFormatter';

const getAllVisibleSections = (selectedAolDataItem: AolDataItem, vessel: Vessel): NoonReportSectionEnum[] => {
    const allSections = getSectionsInColumn(1).concat(getSectionsInColumn(2), getSectionsInColumn(3));
    return allSections.filter((o) => isSectionVisible(o, selectedAolDataItem, vessel));
};

export const isAolCompleted = (aolDataItem: AolDataItem, vessel: Vessel) => {
    if (!aolDataItem?.noonReport) return false;
    const sectionEnumsToInclude = getAllVisibleSections(aolDataItem, vessel);

    return getAllAolSections(aolDataItem.noonReport)
        .map((o) => o.sectionData)
        .filter((o) => o && sectionEnumsToInclude.includes(o.section))
        .every((o) => o.isCompleted);
};

export const isAolApproved = (aolDataItem: AolDataItem, vessel: Vessel): boolean => {
    if (!aolDataItem?.noonReport) return false;
    const sectionEnumsToInclude = getAllVisibleSections(aolDataItem, vessel);
    return getAllAolSections(aolDataItem.noonReport)
        .map((o) => o.sectionData)
        .filter((o) => o && sectionEnumsToInclude.includes(o.section))
        .every((o) => o.isApproved);
};

export const handleCompleteSection = (
    selectedAolDataItem: AolDataItem,
    imoNumber: number,
    sectionData: NoonReportSectionData,
    section: NoonReportSectionEnum,
    isSectionToBeCompleted: boolean,
    vessel: Vessel,
    updateSectionStatus: (aolSectionDataUpdate: AolSectionDataUpdateDto) => void
) => {
    if (!selectedAolDataItem) return;

    sectionData.isCompleted = isSectionToBeCompleted;

    const isSectionToBeApproved = false;
    let isAolCompletedResponse = isAolCompleted(selectedAolDataItem, vessel);
    const isAolApprovedResponse = isAolApproved(selectedAolDataItem, vessel);
    if (isAolApprovedResponse) {
        isAolCompletedResponse = true;
    }

    const aolSectionDataUpdate = new AolSectionDataUpdateDtoFactory().createDto(
        imoNumber,
        section,
        isAolCompletedResponse,
        isAolApprovedResponse,
        isSectionToBeCompleted,
        isSectionToBeApproved,
        selectedAolDataItem.noonReport?.metadata.noonReportGuid
    );

    updateSectionStatus(aolSectionDataUpdate);
};

export const handleApproveSection = (
    selectedAolDataItem: AolDataItem,
    imoNumber: number,
    sectionData: NoonReportSectionData,
    section: NoonReportSectionEnum,
    isSectionToBeApproved: boolean,
    vessel: Vessel,
    updateSectionStatus: (aolSectionDataUpdate: AolSectionDataUpdateDto) => void
) => {
    if (!selectedAolDataItem) return;

    sectionData.isApproved = isSectionToBeApproved;

    let isSectionToBeCompleted = true;
    if (!isSectionToBeApproved) {
        sectionData.isCompleted = false;
        isSectionToBeCompleted = false;
    }
    let isAolCompletedResponse = isAolCompleted(selectedAolDataItem, vessel);
    const isAolApprovedResponse = isAolApproved(selectedAolDataItem, vessel);
    if (isAolApprovedResponse) {
        isAolCompletedResponse = true;
    }

    const aolSectionDataUpdate = new AolSectionDataUpdateDtoFactory().createDto(
        imoNumber,
        section,
        isAolCompletedResponse,
        isAolApprovedResponse,
        isSectionToBeCompleted,
        isSectionToBeApproved,
        selectedAolDataItem.noonReport?.metadata.noonReportGuid
    );

    updateSectionStatus(aolSectionDataUpdate);
};

export const getAolTabInfo = (aolDataItem: AolDataItem, maxLength?: number) => {
    let tabInfoText = '';
    if (!aolDataItem) return tabInfoText;

    const portName = aolDataItem.portName || '???';
    let prefix = '';
    let suffix = '';

    if (aolDataItem?.reportType === NoonReportTypeEnum.Port) {
        tabInfoText = portName;
    } else if (aolDataItem?.reportType === NoonReportTypeEnum.Sea) {
        tabInfoText = 'At Sea';
    } else if (aolDataItem?.reportType === NoonReportTypeEnum.Departure) {
        prefix = 'Departure ';
        tabInfoText = prefix + portName;
    } else if (aolDataItem?.reportType === NoonReportTypeEnum.Arrival) {
        prefix = 'Arrival ';
        tabInfoText = prefix + portName;
    }

    if (aolDataItem.aolDataItemProperties.isDryDockingAolReport) {
        suffix = ' (DD)';
    } else if (aolDataItem.isReportAtNoon) {
        suffix = ' (noon)';
    }
    tabInfoText += suffix;

    if (maxLength && tabInfoText.length > maxLength) {
        const availableLength = maxLength - prefix.length - suffix.length - 2; // 2 for ".."
        if (availableLength > 0) {
            tabInfoText = prefix + portName.slice(0, availableLength).trimEnd() + '..' + suffix;
        }
    }

    return tabInfoText;
};

const isNextDay = (date: Date, isToDate: boolean) => (isToDate ? getDateHour(date) <= 12 : getDateHour(date) < 12);

export const getTimelineText = (aolDataItem: AolDataItem) => {
    if (aolDataItem.reportFromTimeLocalTime && aolDataItem.reportToTimeLocalTime) {
        const fromTime = formatDateTime(aolDataItem.reportFromTimeLocalTime, DateTimeFormat.timeFormats.timeOnly);
        const toTime = formatDateTime(aolDataItem.reportToTimeLocalTime, DateTimeFormat.timeFormats.timeOnly);
        const fromString = isNextDay(aolDataItem.reportFromTimeLocalTime, false) ? `${fromTime}` : `${fromTime} (d-1)`;
        const toString = isNextDay(aolDataItem.reportToTimeLocalTime, true) ? `${toTime}` : `${toTime} (d-1)`;
        return `${fromString} - ${toString}`;
    } else {
        return `${aolDataItem.reportFromTimeLocalTime}`;
    }
};

export const getEndTimeText = (aolDataItem: AolDataItem) => {
    if (aolDataItem?.reportToTimeLocalTime) {
        const toTime = formatDateTime(aolDataItem.reportToTimeLocalTime, DateTimeFormat.prettifyFormats.dateTime);
        return `${toTime}`;
    } else {
        return '';
    }
};
